/**
 * Sanal hesap paylaşımının iki ekranı (`lib/sanalPaylasim.ts`):
 *  - `PaylasimPaneli`: bir kuralın linki (kopyala), QR'ı ve dosyası;
 *  - `GelenPaylasim`: dışarıdan gelen kuralın özeti ve açık onay —
 *    dışarıdan gelen hiçbir şey kendiliğinden eklenmez.
 */

import { useState } from "react";
import type { SanalHesapTanimi } from "@parafon/contracts/local-api";
import { downloadBlob } from "../lib/files";
import { formatIsoDate } from "../lib/format";
import { kuralOzeti } from "../lib/sanalHesap";
import { paylasimDosyaAdi, paylasimLinki, paylasimMetni } from "../lib/sanalPaylasim";
import type { PaylasilanKural } from "../lib/sanalPaylasim";
import { HesapAdi } from "../ui/HesapAdi";
import { Button, Notice, TextInput } from "../ui/primitives";
import { QrResmi } from "./KasaQr";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { M } from "../dil";

export function PaylasimPaneli({ tanim }: { readonly tanim: SanalHesapTanimi }) {
  const link = paylasimLinki(tanim);
  const [kopyalandi, setKopyalandi] = useState<boolean | null>(null);
  const [qr, setQr] = useState(false);
  const kopyala = (): void => {
    const pano = globalThis.navigator?.clipboard;
    if (pano === undefined) {
      setKopyalandi(false);
      return;
    }
    pano.writeText(link).then(
      () => setKopyalandi(true),
      () => setKopyalandi(false),
    );
  };
  return (
    <section className="mt-3 w-full space-y-2 border-t border-line pt-3" aria-label={M.sanal.paylasim.panelEtiket(tanim.ad)}>
      <p className="text-[12px] text-muted">
        {M.sanal.paylasim.aciklama}
        {tanim.paraAkisi.tur === "defter" && M.sanal.paylasim.defterNotu}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <TextInput
          readOnly
          aria-label={M.sanal.paylasim.link}
          value={link}
          className="min-w-0 flex-1 text-[12px]"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button onClick={kopyala}>{M.sanal.paylasim.kopyala}</Button>
        <Button onClick={() => setQr((x) => !x)} aria-expanded={qr}>
          {qr ? M.sanal.paylasim.qrGizle : M.sanal.paylasim.qrGoster}
        </Button>
        <Button
          onClick={() =>
            downloadBlob(new Blob([paylasimMetni(tanim)], { type: "application/json" }), paylasimDosyaAdi(tanim))
          }
        >
          {M.sanal.paylasim.dosyaIndir}
        </Button>
      </div>
      {kopyalandi === true && <p role="status" className="text-[12px] text-muted">{M.sanal.paylasim.kopyalandi}</p>}
      {kopyalandi === false && (
        <p role="status" className="text-[12px] text-muted">
          {M.sanal.paylasim.panoIzinYok}
        </p>
      )}
      {qr && <QrResmi metin={link} kip="Byte" etiket={M.sanal.paylasim.qrEtiket(tanim.ad)} />}
    </section>
  );
}

/** Özet için geçici tanım: B akışında hesap listesi boş. */
function ozetTanimi(k: PaylasilanKural): SanalHesapTanimi {
  const pa = k.paraAkisi.tur === "defter" ? { tur: "defter" as const, accountIds: [] } : k.paraAkisi;
  return { ...(k as Omit<PaylasilanKural, "paraAkisi">), id: "", paraAkisi: pa } as SanalHesapTanimi;
}

export function GelenPaylasim({
  kural,
  ad,
  bekliyor,
  onEkle,
  onVazgec,
  kilit = null,
}: {
  readonly kural: PaylasilanKural;
  /** Gelir modeli: kilitli olan şey (kural eklenmez, özeti gösterilir). */
  readonly kilit?: string | null;
  /** Kasada aynı ad varsa numaralı hâli. */
  readonly ad: string;
  readonly bekliyor: boolean;
  readonly onEkle: () => void;
  readonly onVazgec: () => void;
}) {
  const t = ozetTanimi(kural);
  const b = kural.paraAkisi.tur === "defter";
  return (
    <section className="pf-card space-y-2 p-4" aria-labelledby="gelen-paylasim-baslik">
      <h2 id="gelen-paylasim-baslik" className="text-[14px] font-semibold text-ink">
        {M.sanal.paylasim.gelenBaslik}
      </h2>
      <p className="text-[13px] text-ink">
        <HesapAdi renk={kural.renk} className="font-semibold">
          {ad}
        </HesapAdi>
        {ad !== kural.ad.trim() && <span className="text-muted">{M.sanal.paylasim.zatenVar(kural.ad.trim())}</span>}
      </p>
      <p className="text-[12px] text-muted">{kuralOzeti(t)}</p>
      <p className="text-[12px] text-muted">
        {formatIsoDate(kural.baslangic)} – {kural.bitis == null ? M.sanal.paylasim.bugun : formatIsoDate(kural.bitis)} ·{" "}
        {kural.varliklar.map((v) => v.instrumentId).join(", ")}
      </p>
      {b && (
        <Notice tone="info" title={M.sanal.paylasim.bBaslik}>
          <p>{M.sanal.paylasim.bMetin}</p>
        </Notice>
      )}
      <p className="text-[12px] text-muted">{M.sanal.paylasim.cihazinizda}</p>
      {kilit !== null && <KilitliKabiliyet ne={kilit} aciklama={M.sanal.paylasim.eklenmedi} />}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" disabled={bekliyor || kilit !== null} onClick={onEkle}>
          {b ? M.sanal.paylasim.ekleUc : M.sanal.paylasim.ekle}
        </Button>
        <Button onClick={onVazgec}>{M.sanal.paylasim.vazgec}</Button>
      </div>
    </section>
  );
}
